import { Ellipsis, Files, List } from 'lucide-react'
import * as React from 'react'
import type {
	AnalysisOverviewItem,
	AnalysisStatus,
	SourceTypeEnum,
	SummaryDetailLevel,
} from '@/client/types.gen'
import { AnalysisTileCollapsibleSection } from '@/components/project/AnalysisTileCollapsibleSection'
import {
	listEnabledBpmnOptionKeys,
	normalizeBpmnGenerationOptions,
} from '@/components/project/analysis-modal/bpmnOptions'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'

type StatusVisual = {
	key: 'draft' | 'inProgress' | 'done' | 'failed' | 'readyForReview'
	className: string
}

function statusVisual(status: AnalysisStatus): StatusVisual {
	switch (status) {
		case 'draft':
			return { key: 'draft', className: 'bg-gray-100 text-gray-800 border border-gray-200' }
		case 'summary_ready':
			return {
				key: 'readyForReview',
				className: 'bg-muted/80 text-muted-foreground border border-border/80',
			}
		case 'generating_summary':
		case 'generating_model':
		case 'completed':
		case 'model_ready':
			return { key: 'done', className: 'bg-[#8c52ff]/15 text-[#6b3fd4] border border-[#8c52ff]/40' }
		case 'failed':
			return { key: 'failed', className: 'bg-[#ff3131]/10 text-[#c41e1e] border border-[#ff3131]/35' }
		default:
			return {
				key: 'inProgress',
				className: 'bg-[#ff914d]/15 text-[#c45c00] border border-[#ff914d]/40',
			}
	}
}

function sourceTypeBadgeClass(sourceType: SourceTypeEnum | undefined): string {
	const kind = sourceType ?? 'main'
	if (kind === 'context') {
		return 'bg-muted/80 text-muted-foreground border border-border/80'
	}
	return 'bg-[#8c52ff]/10 text-[#5c3dc4] border border-[#8c52ff]/35'
}

function detailLevelBadgeClass(level: SummaryDetailLevel | undefined): string {
	void level
	return 'bg-muted/80 text-muted-foreground border border-border/80'
}

export interface AnalysisTileProps extends React.HTMLAttributes<HTMLDivElement> {
	analysis: AnalysisOverviewItem
	canEditName?: boolean
	canDeleteAnalysis?: boolean
	/** When key changes, expand/collapse all tile sections to `sectionsExpandedFromSync` */
	sectionsSyncKey?: number
	sectionsExpandedFromSync?: boolean
	onRename?: () => void
	onDelete?: () => void
}

const AnalysisTile = React.forwardRef<HTMLDivElement, AnalysisTileProps>(
	(
		{
			analysis,
			className,
			canEditName = false,
			canDeleteAnalysis = false,
			sectionsSyncKey = 0,
			sectionsExpandedFromSync = false,
			onRename,
			onDelete,
			onClick,
			...props
		},
		ref
	) => {
		const { t } = useTranslation()
		const [sourcesOpen, setSourcesOpen] = React.useState(false)
		const [enrichmentOpen, setEnrichmentOpen] = React.useState(false)

		const showActionsMenu = (canEditName && onRename) || (canDeleteAnalysis && onDelete)

		// Bumps on global expand/collapse; memo gives one object dep so the effect re-runs when needed
		const sectionsLayoutSync = React.useMemo(
			() => ({ key: sectionsSyncKey, open: sectionsExpandedFromSync }),
			[sectionsSyncKey, sectionsExpandedFromSync]
		)
		React.useEffect(() => {
			setSourcesOpen(sectionsLayoutSync.open)
			setEnrichmentOpen(sectionsLayoutSync.open)
		}, [sectionsLayoutSync])

		const status = statusVisual(analysis.status)
		const languageCode = analysis.language ?? 'en'
		const languageLabelKey = `languages.${languageCode}`
		const languageLabel = t(languageLabelKey as never)
		const sourceRows = analysis.sources ?? []
		const enabledEnrichmentKeys = listEnabledBpmnOptionKeys(
			normalizeBpmnGenerationOptions(analysis.bpmn_generation_options)
		)
		const sourceListKeys = React.useMemo(() => {
			const counts: Record<string, number> = {}
			return sourceRows.map((row) => {
				const st = row.source_type ?? 'main'
				const dedupeKey = `${row.name}:${st}`
				const n = (counts[dedupeKey] ?? 0) + 1
				counts[dedupeKey] = n
				return `${analysis.id}:src:${dedupeKey}#${n}`
			})
		}, [analysis.id, sourceRows])

		return (
			<Card
				ref={ref}
				className={cn(
					'group hover:shadow-lg hover:scale-[1.02] transition-all duration-300 ease-out cursor-pointer',
					className
				)}
				onClick={onClick}
				{...props}
			>
				<CardHeader className="space-y-2">
					<div className="flex items-start justify-between gap-2">
						<div className="min-w-0 flex-1">
							<CardTitle className="text-lg pr-1">{analysis.name}</CardTitle>
							{analysis.description && (
								<p className="text-sm text-gray-500 mt-1 line-clamp-2">{analysis.description}</p>
							)}
						</div>
						{showActionsMenu && (
							<DropdownMenu>
								<DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
									<button
										type="button"
										aria-label={t('projectTile.openActions')}
										className="rounded p-1 transition-all duration-300 ease-in-out hover:bg-gray-100 focus:outline-none shrink-0"
									>
										<Ellipsis className="h-5 w-5" />
									</button>
								</DropdownMenuTrigger>

								<DropdownMenuContent
									align="end"
									side="bottom"
									className="w-36"
									onClick={(e) => e.stopPropagation()}
								>
									{canEditName && onRename && (
										<DropdownMenuItem onSelect={() => onRename()}>{t('projectTile.rename')}</DropdownMenuItem>
									)}

									{canDeleteAnalysis && onDelete && (
										<DropdownMenuItem variant="destructive" onSelect={() => onDelete()}>
											{t('projectTile.delete')}
										</DropdownMenuItem>
									)}
								</DropdownMenuContent>
							</DropdownMenu>
						)}
					</div>
					<div className="flex items-center gap-2 flex-wrap">
						<span
							className={cn(
								'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
								status.className
							)}
						>
							{t(`pages.projectDetail.analyses.status.${status.key}` as never)}
						</span>
						<span
							className={cn(
								'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
								detailLevelBadgeClass(analysis.summary_detail_level)
							)}
						>
							{t(
								`pages.projectDetail.analyses.detailLevel.${analysis.summary_detail_level ?? 'standard'}` as never
							)}
						</span>
						<span
							className={cn(
								'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
								detailLevelBadgeClass(undefined)
							)}
						>
							{languageLabel === languageLabelKey ? languageCode : languageLabel}
						</span>
						{analysis.created_at && (
							<span className="text-xs text-gray-400 ml-auto">
								{new Date(analysis.created_at).toLocaleDateString()}
							</span>
						)}
					</div>
				</CardHeader>
				<CardContent className="pt-0">
					{sourceRows.length > 0 && (
						<AnalysisTileCollapsibleSection
							icon={Files}
							title={t('pages.projectDetail.analyses.sourcesTitle')}
							countLabel={`(${sourceRows.length})`}
							open={sourcesOpen}
							onToggle={() => setSourcesOpen((o) => !o)}
						>
							{sourceRows.map((row, i) => {
								const st = row.source_type ?? 'main'
								return (
									<span
										key={sourceListKeys[i]}
										className="inline-flex max-w-full items-center gap-1.5 rounded-md bg-muted/70 px-2.5 py-1 text-xs font-medium"
									>
										<span className="min-w-0 break-words text-foreground/90">{row.name}</span>
										<span
											className={cn(
												'shrink-0 rounded px-1.5 py-0.5 text-[0.65rem] font-medium leading-none',
												sourceTypeBadgeClass(st)
											)}
										>
											{t(`pages.projectDetail.analyses.sourceType.${st}` as 'never')}
										</span>
									</span>
								)
							})}
						</AnalysisTileCollapsibleSection>
					)}
					{enabledEnrichmentKeys.length > 0 && (
						<AnalysisTileCollapsibleSection
							icon={List}
							title={t('pages.projectDetail.analyses.enrichmentTitle')}
							countLabel={`(${enabledEnrichmentKeys.length})`}
							open={enrichmentOpen}
							onToggle={() => setEnrichmentOpen((o) => !o)}
						>
							{enabledEnrichmentKeys.map((optionKey) => (
								<span
									key={optionKey}
									className="inline-flex items-center rounded-md bg-muted/70 px-2.5 py-1 text-xs font-medium text-foreground/90"
								>
									{t(`pages.projectDetail.modal.bpmnOptions.options.${optionKey}.label` as never)}
								</span>
							))}
						</AnalysisTileCollapsibleSection>
					)}
				</CardContent>
			</Card>
		)
	}
)
AnalysisTile.displayName = 'AnalysisTile'

export { AnalysisTile }
